import { createFileRoute } from "@tanstack/react-router";
import React from "react";
import { Controller } from "react-hook-form";

import { CheckboxGroupField } from "~/components/form/checkbox-group-field";
import { DiscoveryStep } from "~/components/ui/discovery-step";
import { FormField } from "~/components/ui/form-field";
import { RadioGroup, RadioGroupItem } from "~/components/ui/radio-group";
import { useDiscoveryForm } from "~/lib/hooks/use-discovery-form";
import {
  CoachDemographicsPreferenceSchema,
  coachAgesField,
  coachDemographicsPreferenceStep,
  coachGenderField,
} from "~/lib/schemas/coach-discovery";
import type { CoachDemographicsPreferenceForm } from "~/lib/schemas/coach-discovery";
import { createNoIndexMeta } from "~/lib/shared/meta";

export const Route = createFileRoute("/_public/discovery/steps/5/")({
  head: () =>
    createNoIndexMeta(
      "Step 5: Coach Demographics Preference – Dream Coach Match",
      "Set your coach demographics preferences in our discovery process",
    ),
  component: DiscoveryStep5,
});

function DiscoveryStep5() {
  const { form, handleNextStep, handlePreviousStep } = useDiscoveryForm({
    step: 5,
    schema: CoachDemographicsPreferenceSchema,
  });
  const { control, handleSubmit, formState } = form;
  const { errors, isValid, isSubmitting } = formState;

  const onSubmit = (_data: CoachDemographicsPreferenceForm) => {
    handleNextStep();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DiscoveryStep.Title title={coachDemographicsPreferenceStep.title} />
      <DiscoveryStep.Card>
        <FormField
          labelSize="xl"
          error={errors.coachGender?.message}
          label={coachGenderField.subTitle || ""}
        >
          <Controller
            name="coachGender"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <RadioGroup
                value={field.value || ""}
                onValueChange={field.onChange}
                className="flex flex-col gap-y-4"
              >
                {coachGenderField.options?.map(({ value, label }) => (
                  <RadioGroupItem key={value} value={value} label={label} />
                ))}
              </RadioGroup>
            )}
          />
        </FormField>
      </DiscoveryStep.Card>
      <DiscoveryStep.Card className="mt-6">
        <FormField
          labelSize="xl"
          error={errors.coachAges?.message}
          label={coachAgesField.subTitle}
        >
          <CheckboxGroupField
            name="coachAges"
            control={control}
            options={coachAgesField.options || []}
            exclusiveOptions={["No preference"]}
          />
        </FormField>
      </DiscoveryStep.Card>
      <DiscoveryStep.Control
        prev={handlePreviousStep}
        type="submit"
        disabled={!isValid}
        loading={isSubmitting}
      />
    </form>
  );
}
